import { isInPeriode } from './periode';

// Niveaux des attendus rencontrés dans une carte (attendu, notion, thème) :
// '6eme'… venus de `attenduNiveau`, envoyé par le back dans chaque ligne de
// résultat d'un attendu (2026-09-29). Les lignes de savoir-faire (ancien
// régime) et les réponses sans la clé n'ajoutent rien. Sert à la pastille
// « 6e » d'une carte dont les attendus ne sont pas du niveau de la classe.
const ajouterNiveaux = (item, lignes) => {
    lignes.forEach((r) => {
        if (r.attenduNiveau && !item.niveaux.includes(r.attenduNiveau)) item.niveaux.push(r.attenduNiveau);
    });
};

// Utilise l'objet retourné par l'API. Une évaluation est créée pour chaque élève de la classe.
// Dans chaque évaluation, l'élève peut être absent (ABS) ou noté.

// Cette fonction récupère toutes les évaluations associées à un élève afin de collecter la liste des évaluations
// pour une classe donnée.
export const getReussiteSavoir = (classeDetail, activePeriode, seuil = 5) => {
    const savoirs = [];

    classeDetail.eleves.forEach((eleve) => {
        const processedSavoirs = new Set();

        eleve.resultats.forEach(element => {
            // Filtrer par période active
            if (!isInPeriode(element.evalDate, activePeriode)) return;

            if (element.scores && element.scores !== 0) {
                const savoirId = element.savoirFaireId || element.attenduId;
                const savoirNom = element.savoirTitre || element.attenduTitre;
                const isSavoirFaire = !!element.savoirFaireId;
                const uniqueSavoirKey = `${isSavoirFaire ? 'savoir' : 'attendu'}-${savoirId}`;

                if (!processedSavoirs.has(uniqueSavoirKey)) {
                    const resultsForSavoir = eleve.resultats.filter(res => {
                        const resId = res.savoirFaireId || res.attenduId;
                        const resIsSavoirFaire = !!res.savoirFaireId;
                        return resId === savoirId && resIsSavoirFaire === isSavoirFaire && res.scores !== null;
                    });

                    const scoresTotal = resultsForSavoir.reduce((total, res) => total + res.scores, 0);
                    const scoreMoyen = scoresTotal / resultsForSavoir.length;

                    const savoirIndex = savoirs.findIndex(item => item.uniqueSavoirKey === uniqueSavoirKey);

                    if (savoirIndex === -1) {
                        savoirs.push({
                            uniqueSavoirKey,
                            savoirId,
                            savoirNom,
                            nom: savoirNom,
                            evalDate: element.evalDate,
                            dernierEval: element.evalDate,
                            evalDates: new Set([element.evalDate]),
                            scoresTotal,
                            nbEvalSatisfaisant: scoreMoyen >= seuil ? 1 : 0,
                            nbEvalEnCours: (scoreMoyen > 0 && scoreMoyen < seuil) ? 1 : 0,
                            nbEvalFragile: (scoreMoyen > 0 && scoreMoyen < seuil) ? 1 : 0,
                            nbSavoirs: resultsForSavoir.length,
                            themeNom: element.themeNom,
                            nbEleves: 1,
                            isSavoirFaire,
                            niveaux: [],
                        });
                    } else {
                        const dateActuelle = new Date(element.evalDate);
                        const dateDerniere = new Date(savoirs[savoirIndex].dernierEval);
                        if (dateActuelle > dateDerniere) {
                            savoirs[savoirIndex].dernierEval = element.evalDate;
                        }
                        savoirs[savoirIndex].evalDates.add(element.evalDate);
                        savoirs[savoirIndex].scoresTotal += scoresTotal;
                        savoirs[savoirIndex].nbSavoirs += resultsForSavoir.length;
                        savoirs[savoirIndex].nbEleves += 1;
                        if (scoreMoyen >= seuil) {
                            savoirs[savoirIndex].nbEvalSatisfaisant += 1;
                        } else if (scoreMoyen > 0) {
                            savoirs[savoirIndex].nbEvalFragile += 1;
                        }
                    }
                    ajouterNiveaux(savoirIndex === -1 ? savoirs[savoirs.length - 1] : savoirs[savoirIndex], resultsForSavoir);

                    processedSavoirs.add(uniqueSavoirKey);
                }
            }
        });
    });

    // Calcul moyenneClasse pour chaque savoir
    savoirs.forEach(s => {
        s.moyenneClasse = s.nbSavoirs > 0 ? Math.round((s.scoresTotal / s.nbSavoirs) * 100) / 100 : 0;
    });

    // Tri par date décroissante
    savoirs.sort((a, b) => new Date(b.dernierEval) - new Date(a.dernierEval));

    return savoirs;
};

// ─── Agrégation par DESCRIPTEUR (notion) ───
export const getReussiteDescripteur = (classeDetail, activePeriode) => {
  const descripteurs = [];

  classeDetail.eleves.forEach((eleve) => {
    const processedDesc = new Set();

    eleve.resultats.forEach(element => {
      // Filtre période
      if (!isInPeriode(element.evalDate, activePeriode)) return;
      if (!element.scores || element.scores === 0) return;
      if (!element.descripteurId) return;

      const descKey = `descripteur-${element.descripteurId}`;

      if (!processedDesc.has(descKey)) {
        // Tous les scores de cet élève dans ce descripteur
        const results = eleve.resultats.filter(r =>
          r.descripteurId === element.descripteurId && r.scores && r.scores !== 0
        );
        const total = results.reduce((s, r) => s + r.scores, 0);
        const moyenne = total / results.length;

        const idx = descripteurs.findIndex(d => d.uniqueKey === descKey);

        if (idx === -1) {
          descripteurs.push({
            uniqueKey: descKey,
            id: element.descripteurId,
            nom: element.descripteurNom,
            themeNom: element.themeNom,
            dernierEval: element.evalDate,
            evalDates: new Set([element.evalDate]),
            scoresTotal: total,
            nbScores: results.length,
            nbEleves: 1,
            nbEvalSatisfaisant: moyenne >= 5 ? 1 : 0,
            nbEvalEnCours: (moyenne > 3 && moyenne < 5) ? 1 : 0,
            nbEvalFragile: (moyenne >= 1 && moyenne <= 3) ? 1 : 0,
            niveaux: [],
          });
        } else {
          const dateAct = new Date(element.evalDate);
          if (dateAct > new Date(descripteurs[idx].dernierEval)) {
            descripteurs[idx].dernierEval = element.evalDate;
          }
          descripteurs[idx].evalDates.add(element.evalDate);
          descripteurs[idx].scoresTotal += total;
          descripteurs[idx].nbScores += results.length;
          descripteurs[idx].nbEleves += 1;
          if (moyenne >= 5) descripteurs[idx].nbEvalSatisfaisant += 1;
          else if (moyenne > 3 && moyenne < 5) descripteurs[idx].nbEvalEnCours += 1;
          else if (moyenne >= 1) descripteurs[idx].nbEvalFragile += 1;
        }
        ajouterNiveaux(idx === -1 ? descripteurs[descripteurs.length - 1] : descripteurs[idx], results);

        processedDesc.add(descKey);
      }
    });
  });

  // Calcul moyenne classe
  descripteurs.forEach(d => {
    d.moyenneClasse = d.nbScores > 0 ? Math.round((d.scoresTotal / d.nbScores) * 100) / 100 : 0;
  });

  descripteurs.sort((a, b) => Date.parse(b.dernierEval) - Date.parse(a.dernierEval));
  return descripteurs;
};

// ─── Agrégation par THEME ───
export const getReussiteTheme = (classeDetail, activePeriode) => {
  const themes = [];

  classeDetail.eleves.forEach((eleve) => {
    const processedThemes = new Set();

    eleve.resultats.forEach(element => {
      // Filtre période
      if (!isInPeriode(element.evalDate, activePeriode)) return;
      if (!element.scores || element.scores === 0) return;
      if (!element.themeNom) return;

      const themeKey = `theme-${element.themeNom}`;

      if (!processedThemes.has(themeKey)) {
        // Tous les scores de cet élève dans ce thème
        const results = eleve.resultats.filter(r =>
          r.themeNom === element.themeNom && r.scores && r.scores !== 0
        );
        const total = results.reduce((s, r) => s + r.scores, 0);
        const moyenne = total / results.length;

        const idx = themes.findIndex(t => t.uniqueKey === themeKey);

        if (idx === -1) {
          themes.push({
            uniqueKey: themeKey,
            nom: element.themeNom,
            dernierEval: element.evalDate,
            evalDates: new Set([element.evalDate]),
            scoresTotal: total,
            nbScores: results.length,
            nbEleves: 1,
            nbEvalSatisfaisant: moyenne >= 5 ? 1 : 0,
            nbEvalEnCours: (moyenne > 3 && moyenne < 5) ? 1 : 0,
            nbEvalFragile: (moyenne >= 1 && moyenne <= 3) ? 1 : 0,
            niveaux: [],
          });
        } else {
          const dateAct = new Date(element.evalDate);
          if (dateAct > new Date(themes[idx].dernierEval)) {
            themes[idx].dernierEval = element.evalDate;
          }
          themes[idx].evalDates.add(element.evalDate);
          themes[idx].scoresTotal += total;
          themes[idx].nbScores += results.length;
          themes[idx].nbEleves += 1;
          if (moyenne >= 5) themes[idx].nbEvalSatisfaisant += 1;
          else if (moyenne > 3 && moyenne < 5) themes[idx].nbEvalEnCours += 1;
          else if (moyenne >= 1) themes[idx].nbEvalFragile += 1;
        }
        ajouterNiveaux(idx === -1 ? themes[themes.length - 1] : themes[idx], results);

        processedThemes.add(themeKey);
      }
    });
  });

  // Calcul moyenne classe
  themes.forEach(t => {
    t.moyenneClasse = t.nbScores > 0 ? Math.round((t.scoresTotal / t.nbScores) * 100) / 100 : 0;
  });

  themes.sort((a, b) => Date.parse(b.dernierEval) - Date.parse(a.dernierEval));
  return themes;
};

// Récuperer les savoirs non évaluée dans l'année scolaire en cours.
export const getNonEvaluatedSavoirsFromThemeDetail = (themeDetail, eleves) => {
    // On crée des ensembles pour garder la trace des savoirs évalués
    const evaluatedSavoirs = {
      savoir: new Set(),
      attendu: new Set()
    };
  
    // Parcourt tous les élèves et leurs résultats
    eleves?.eleves?.forEach(eleve => {
      eleve.resultats.forEach(result => {
        // Considère le résultat seulement s'il a été évalué (score non nul et non 0)
        if (result.scores && result.scores !== 0) {
          if (result.savoirId) {
            evaluatedSavoirs.savoir.add(result.savoirId);
          } else if (result.attenduId) {
            evaluatedSavoirs.attendu.add(result.attenduId);
          }
        }
      });
    });
  
    // Pour chaque thème, on filtre les descripteurs pour ne garder que les éléments non évalués
    const filteredThemeDetail = themeDetail.map(theme => {
      // Copie superficielle du thème pour ne pas modifier l'original
      const newTheme = { ...theme };
  
      if (newTheme.descripteur && Array.isArray(newTheme.descripteur)) {
        newTheme.descripteur = newTheme.descripteur.map(descriptor => {
          const newDescriptor = { ...descriptor };
  
          // Filtrer les savoirs-faire (cas des savoir-faire évalués)
          if (newDescriptor.savoirs_faires && Array.isArray(newDescriptor.savoirs_faires)) {
            newDescriptor.savoirs_faires = newDescriptor.savoirs_faires.filter(item => {
              // On suppose que l'ID de chaque savoir-faire est dans item.id
              return !evaluatedSavoirs.savoir.has(item.id);
            });
          }
  
          // Filtrer les attendus
          if (newDescriptor.attendus && Array.isArray(newDescriptor.attendus)) {
            newDescriptor.attendus = newDescriptor.attendus.filter(item => {
              // On suppose que l'ID de chaque attendu est dans item.id
              return !evaluatedSavoirs.attendu.has(item.id);
            });
          }
  
          return newDescriptor;
        });
      }
      return newTheme;
    });
  
    return filteredThemeDetail;
  };


// Cette fonction récupère toutes les évaluations associées à un élève afin de collecter la liste des évaluations
// pour une classe donnée.
export const getEvaluations = (classeDetail, activePeriode,seuil) => {
    const evals = [];
    classeDetail.eleves.forEach((eleve) => {
    
        const processedEvals = new Set(); // Pour suivre les évaluations déjà comptées pour cet élève

        eleve.resultats.forEach(element => {
             // Filtrer par date : on ne prend en compte que les résultats dont evalDate est dans la période active
             if (!isInPeriode(element.evalDate, activePeriode)) return;
            const evalIndex = evals.findIndex(item => item.evalId === element.evalId);

            if (!processedEvals.has(element.evalId)) {
                // Regroupe tous les résultats de l'élève pour l'évaluation actuelle
                const resultsForEval = eleve.resultats.filter(res => res.evalId === element.evalId);

                // Calcule le score moyen pour cette évaluation
                const scoresTotal = resultsForEval.reduce((total, res) => total + res.scores, 0);
                const scoreMoyen = scoresTotal / resultsForEval.length;
              
                // Si l'évaluation n'existe pas dans le tableau, l'ajoute
                if (evalIndex === -1) {
                    evals.push({
                        evalId: element.evalId,
                        evalNom: element.evalTitre,
                        evalDate: element.evalDate,
                        classeNom: element.classeNom,
                        groupNom: element.groupNom,
                        profCreateurNom : element.profCreateurNom,
                        profCreateurPrenom : element.profCreateurPrenom,
                        scoresTotal: scoresTotal,
                        nbEvalSatisfaisant: scoreMoyen >= seuil ? 1 : 0,
                        nbEvalFragile: (scoreMoyen >0 && scoreMoyen < seuil) ? 1 : 0,
                        nbSavoirs: resultsForEval.length,
                        nbEleves: 1
                    });
                } else {
                    
                    // Met à jour les données existantes pour l'évaluation
                    evals[evalIndex].scoresTotal += scoresTotal;
                    evals[evalIndex].nbSavoirs += resultsForEval.length;
                   
                    evals[evalIndex].nbEleves +=1;
                    if (scoreMoyen >= seuil) {
                        evals[evalIndex].nbEvalSatisfaisant += 1;
                    } else if(scoreMoyen > 0 ) {
                        evals[evalIndex].nbEvalFragile += 1;
                    }
                }

                processedEvals.add(element.evalId); // Marque cette évaluation comme traitée pour cet élève
            }
        });
    });

    // Tri par date en utilisant la fonction Date.parse pour convertir en timestamp
    evals.sort((a, b) => Date.parse(b.evalDate) - Date.parse(a.evalDate));

  
    return evals;
};
 // Fonction qui formate correctement la date pour l'entrée dans la base de données.
  //  !!!!! à mettre DANS UTILS !!!!
 export function formatDate(dateString) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Ajoute un zéro devant si nécessaire
    const day = date.getDate().toString().padStart(2, '0'); // Ajoute un zéro devant si nécessaire
    return `${year}-${month}-${day}`; // Format ISO 8601
  }

export function formatLettresDate(dateStr){
        const date = new Date(dateStr);
        const options = { year: 'numeric', month: 'long', day: '2-digit' };
        return `Le ${date.toLocaleDateString('fr-FR', options)}`;
    
}